import { MAX_SCREEN_SELECTION } from '@/features/campaigns/constants/campaignOptions';
import { normalizeDetectedOrientation } from '@/features/campaigns/components/ManualBuilder';
import { ScreenSearchFilters } from './sections/ScreenSearchFilters';
import { ScreenResults } from './sections/ScreenResults';
import { ScreensPagination } from './sections/ScreensPagination';
import { SelectedScreenChips } from './sections/SelectedScreenChips';
import type { ScreensStepProps } from './ScreensStep.types';
import { useScreensStep } from './useScreensStep';

type ScreensStepExtendedProps = ScreensStepProps & {
  onSelect: (
    screen: Record<string, unknown>,
    opts?: { source?: 'auto' | 'manual' },
  ) => void;
  onClearAll?: () => void;
  selectionSource?: 'auto' | 'manual' | null;
};

export function ScreensStep({
  campaignData,
  selectedIds,
  selectedMeta,
  onSelect,
  onClearAll,
  error,
}: ScreensStepExtendedProps) {
  const step = useScreensStep({ campaignData });
  const creativeOrientation = normalizeDetectedOrientation(
    campaignData.mediaIds?.[0]?.orientation,
  );

  const citiesLabel =
    campaignData.targetCities.join(', ') || 'your selected cities';

  return (
    <div
      className={`flex w-full flex-col gap-3.5 pb-1 ${
        error
          ? 'rounded-2xl ring-2 ring-amber-300/80 ring-offset-2 ring-offset-[#F3F2F9]'
          : ''
      }`}
    >
      <header className="text-left">
        <h1 className="m-0 text-[18px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
          Select screens
        </h1>
        <p className="mt-0.5 mb-0 max-w-2xl text-[12.5px] leading-snug text-[var(--dash-muted)]">
          Choose displays in{' '}
          <span className="font-semibold text-[var(--dash-ink)]">
            {citiesLabel}
          </span>
          . Mix orientations as needed — up to {MAX_SCREEN_SELECTION} screens.
        </p>
      </header>

      <SelectedScreenChips
        selectedMeta={selectedMeta}
        onSelect={(screen) => onSelect(screen, { source: 'manual' })}
        onClearAll={onClearAll}
      />

      <ScreenSearchFilters
        search={step.search}
        areaQuery={step.areaQuery}
        localityPlace={step.localityPlace}
        city={campaignData.targetCities[0]}
        onSearchChange={step.setSearchValue}
        onApplyLocationFilters={step.applyLocationFilters}
        onClearFilters={step.clearFilters}
        filters={step.filters}
        activeFilterCount={step.activeFilterCount}
        locationTypes={step.locationTypes}
        hierarchy={step.hierarchy}
        venuesLoading={step.venuesQuery.isPending}
        creativeOrientation={creativeOrientation}
        orientationFilter={step.orientationFilter}
        onOrientationFilterChange={step.setOrientationFilter}
        orientationCounts={step.orientationCounts}
      />

      <ScreenResults
        loading={step.query.isPending}
        screens={step.screens}
        selectedIds={selectedIds}
        selectedCount={selectedIds.length}
        creativeOrientation={creativeOrientation}
        orientationFilter={step.orientationFilter}
        onSelect={(screen) => onSelect(screen, { source: 'manual' })}
        onResetOrientationFilter={() => step.setOrientationFilter('ALL')}
      />

      <ScreensPagination
        page={step.page}
        totalPages={step.totalPages}
        onPageChange={step.setPage}
      />
    </div>
  );
}
